/**
 * Locale-prefixed URL helpers for SEO-friendly language routing.
 *
 * Every locale is prefixed: /en/features, /es/features, /ar/features. The
 * prefix is the source of truth for the page language and always wins over
 * the persisted browser preference. Unprefixed paths are still resolved as a
 * compatibility fallback.
 */

export interface LocalePrefixResult {
 /** Canonical locale code (as listed in app.i18n.locales). */
 locale: string;
 /** Path with the locale prefix removed (always starts with "/"). */
 strippedPath: string;
}

function normalizePathLike(input: string | undefined | null): string {
 const raw = String(input ?? "").trim();
 if (!raw) return "/";
 return raw.startsWith("/") ? raw : `/${raw}`;
}

export function pickLocaleRecord<T>(
 map: Record<string, T> | undefined,
 locale: string | undefined | null,
): T | undefined {
 if (!map || !locale) return undefined;
 const trimmed = locale.trim();
 if (!trimmed) return undefined;
 if (Object.prototype.hasOwnProperty.call(map, trimmed)) {
 return map[trimmed];
 }
 const needle = trimmed.toLowerCase();
 for (const [key, value] of Object.entries(map)) {
 if (key.toLowerCase() === needle) return value;
 }
 return undefined;
}

export function extractLocalePrefix(
 pathname: string | undefined | null,
 supportedLocales: readonly string[] | undefined,
): LocalePrefixResult | null {
 const path = normalizePathLike(pathname);
 const rest = path.slice(1);
 if (!rest) return null;

 const slashIndex = rest.indexOf("/");
 const first = slashIndex === -1 ? rest : rest.slice(0, slashIndex);
 const candidate = first.trim();
 if (!candidate) return null;

 const locales = (supportedLocales ?? []).map(String);
 const found =
 locales.find((locale) => locale === candidate) ??
 locales.find(
 (locale) => locale.toLowerCase() === candidate.toLowerCase(),
 );
 if (!found) return null;

 const remainder = slashIndex === -1 ? "/" : `/${rest.slice(slashIndex +1)}`;
 return { locale: found, strippedPath: remainder };
}

const PLAUSIBLE_PAGE_LOCALE_RE = /^[a-z]{2,3}(?:-[A-Za-z]{2,4})?$/;

export function resolvePageLocale(
 pathname: string | undefined | null,
 supportedLocales: readonly string[] | undefined,
 bundleLocales?: readonly string[],
): string | undefined {
 const strict = extractLocalePrefix(pathname, supportedLocales)?.locale;
 if (strict) return strict;

 const first =
 normalizePathLike(pathname).split("/").filter(Boolean)[0] ?? "";
 if (!first || !PLAUSIBLE_PAGE_LOCALE_RE.test(first)) return undefined;

 const normalized = first.toLowerCase();
 const bundles = bundleLocales ?? [];
 const bundled = bundles.find((locale) => locale.toLowerCase() === normalized);
 if (bundled) return bundled;
 if (bundles.some((locale) => locale.toLowerCase() === normalized)) {
 return normalized;
 }
 return undefined;
}

export function buildLocalePathname(
 pathname: string | undefined | null,
 locale: string | undefined | null,
 supportedLocales: readonly string[] | undefined,
): string {
 const path = normalizePathLike(pathname);
 const target = String(locale ?? "").trim();
 const locales = (supportedLocales ?? []).map(String).filter(Boolean);
 if (!target || !locales.includes(target)) return path;

 const stripped =
 extractLocalePrefix(path, locales)?.strippedPath ?? path;
 return `/${target}${stripped === "/" ? "" : stripped}`;
}

export function buildLocalePathMap(
 pathname: string | undefined | null,
 supportedLocales: readonly string[] | undefined,
): Record<string, string> {
 const locales = (supportedLocales ?? []).map(String).filter(Boolean);
 const stripped =
 extractLocalePrefix(pathname, locales)?.strippedPath ??
 normalizePathLike(pathname);

 const out: Record<string, string> = {};
 for (const locale of locales) {
 out[locale] = `/${locale}${stripped === "/" ? "" : stripped}`;
 }
 return out;
}